import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { FreeItemReward, RewardProgress } from '../types/rewards';
import { fetchProductById } from './productService';

/**
 * Get all active free item rewards ordered by threshold
 */
export const getActiveFreeItemRewards = async (): Promise<FreeItemReward[]> => {
  try {
    const rewardsCollection = collection(db, 'freeItemRewards');
    const rewardsQuery = query(
      rewardsCollection,
      where('isActive', '==', true),
      orderBy('threshold', 'asc')
    );
    
    const snapshot = await getDocs(rewardsQuery);
    
    const rewards = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as FreeItemReward[];
    
    return rewards;
  } catch (error) {
    console.error('Error fetching active free item rewards:', error);
    return [];
  }
};

/**
 * Get all free item rewards (for admin management)
 */
export const getAllFreeItemRewards = async (): Promise<FreeItemReward[]> => {
  try {
    const rewardsCollection = collection(db, 'freeItemRewards');
    const rewardsQuery = query(rewardsCollection, orderBy('threshold', 'asc'));
    
    const snapshot = await getDocs(rewardsQuery);
    
    const rewards = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as FreeItemReward[];
    
    return rewards;
  } catch (error) {
    console.error('Error fetching all free item rewards:', error);
    return [];
  }
};

/**
 * Create a new free item reward
 */
export const createFreeItemReward = async (
  threshold: number,
  productId: string | null,
  milestoneLabel?: string | null
): Promise<string> => {
  try {
    const rewardsCollection = collection(db, 'freeItemRewards');
    
    let newReward: any = {
      threshold,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Only add product details if productId is provided
    if (productId) {
      // Fetch product details
      const product = await fetchProductById(productId);
      if (!product) {
        throw new Error('Product not found');
      }
      
      newReward.productId = productId;
      newReward.productName = product.name;
      newReward.productImage = product.image;
      newReward.milestoneLabel = null; // Clear milestone label when product is linked
    } else {
      // Set to null for thresholds without linked products
      newReward.productId = null;
      newReward.productName = null;
      newReward.productImage = null;
      newReward.milestoneLabel = milestoneLabel || null; // Set milestone label for milestone-only rewards
    }
    
    const docRef = await addDoc(rewardsCollection, newReward);
    return docRef.id;
  } catch (error) {
    console.error('Error creating free item reward:', error);
    throw new Error('Failed to create free item reward');
  }
};

/**
 * Update a free item reward
 */
export const updateFreeItemReward = async (
  rewardId: string,
  updates: Partial<Omit<FreeItemReward, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<void> => {
  try {
    const rewardDoc = doc(db, 'freeItemRewards', rewardId);
    
    const updateData: any = {
      ...updates,
      updatedAt: serverTimestamp(),
    };
    
    // If productId is being updated, fetch new product details
    if (updates.productId) {
      const product = await fetchProductById(updates.productId);
      if (!product) {
        throw new Error('Product not found');
      }
      updateData.productName = product.name;
      updateData.productImage = product.image;
    }
    
    await updateDoc(rewardDoc, updateData);
  } catch (error) {
    console.error('Error updating free item reward:', error);
    throw new Error('Failed to update free item reward');
  }
};

/**
 * Delete a free item reward
 */
export const deleteFreeItemReward = async (rewardId: string): Promise<void> => {
  try {
    const rewardDoc = doc(db, 'freeItemRewards', rewardId);
    await deleteDoc(rewardDoc);
  } catch (error) {
    console.error('Error deleting free item reward:', error);
    throw new Error('Failed to delete free item reward');
  }
};

/**
 * Calculate reward progress for current cart total (cart-based only, no persistent tracking)
 */
export const calculateRewardProgress = async (
  cartTotal: number,
  options?: {
    convertedCartTotal?: number;
    currencyCode?: string;
    getConvertedItemPrice?: (usdPrice: number) => number;
  }
): Promise<RewardProgress> => {
  try {
    const activeRewards = await getActiveFreeItemRewards();
    
    if (activeRewards.length === 0) {
      return {
        currentTotal: cartTotal,
        nextThreshold: null,
        progress: 0,
        earnedRewards: [],
        earnedMilestones: [],
        nextReward: null,
        hasActiveRewards: false,
        nextMilestoneLabel: null,
      };
    }
    
    // Use display currency logic when available for mathematical precision
    const shouldUseDisplayCurrency = options?.convertedCartTotal !== undefined && 
                                   options?.currencyCode !== 'USD' && 
                                   options?.getConvertedItemPrice;
    
    // Function to check if threshold is met with proper precision
    const isThresholdMet = (threshold: number): boolean => {
      if (shouldUseDisplayCurrency) {
        // Use display currency precision - same logic as cart display
        const convertedThreshold = options!.getConvertedItemPrice!(threshold);
        return options!.convertedCartTotal! >= convertedThreshold;
      } else {
        // Use USD logic with tolerance for floating-point precision
        const MILESTONE_TOLERANCE = 0.01; // 1 cent tolerance for rounding precision
        return (cartTotal + MILESTONE_TOLERANCE) >= threshold;
      }
    };
    
    // Find earned product rewards (threshold met and has product linked)
    const earnedRewards = activeRewards.filter(reward => 
      isThresholdMet(reward.threshold) && 
      reward.productId && 
      reward.productId.trim() !== '' // Only include rewards with valid product IDs
    );

    // Find earned milestone rewards (threshold met and no product linked)
    const earnedMilestones = activeRewards.filter(reward => 
      isThresholdMet(reward.threshold) && 
      (!reward.productId || reward.productId.trim() === '') // Include milestones without product IDs
    );
    
    // Function to check if threshold is NOT yet met (for finding next rewards)
    const isThresholdNotMet = (threshold: number): boolean => {
      if (shouldUseDisplayCurrency) {
        // Use display currency precision - same logic as cart display
        const convertedThreshold = options!.getConvertedItemPrice!(threshold);
        return options!.convertedCartTotal! < convertedThreshold;
      } else {
        // Use USD logic with tolerance for floating-point precision
        const MILESTONE_TOLERANCE = 0.01; // 1 cent tolerance for rounding precision
        return (cartTotal + MILESTONE_TOLERANCE) < threshold;
      }
    };
    
    // Find next reward (only those with linked products for display)
    const nextReward = activeRewards.find(reward => 
      isThresholdNotMet(reward.threshold) && 
      reward.productId && 
      reward.productId.trim() !== '' // Only show rewards with valid product IDs
    );
    
    // Find next threshold (including those without products for progress display)
    const nextThresholdReward = activeRewards.find(reward => 
      isThresholdNotMet(reward.threshold)
    );

    let progress = 0;
    const targetReward = nextReward || nextThresholdReward;
    
    if (targetReward) {
      // Check if we're at the threshold
      if (isThresholdMet(targetReward.threshold)) {
        // We're at the threshold, so show 100% progress
        progress = 100;
      } else {
        // Calculate progress toward next reward/milestone
        if (shouldUseDisplayCurrency) {
          // Use display currency precision for progress calculation
          const convertedTarget = options!.getConvertedItemPrice!(targetReward.threshold);
          const convertedCurrent = options!.convertedCartTotal!;
          
          const previousThreshold = activeRewards
            .filter(r => isThresholdMet(r.threshold))
            .map(r => options!.getConvertedItemPrice!(r.threshold))
            .sort((a, b) => b - a)[0] || 0;
          
          const progressRange = convertedTarget - previousThreshold;
          const currentProgress = convertedCurrent - previousThreshold;
          progress = Math.min(100, Math.max(0, (currentProgress / progressRange) * 100));
        } else {
          // Use USD logic
          const previousThreshold = activeRewards
            .filter(r => r.threshold < targetReward.threshold)
            .pop()?.threshold || 0;
          
          const progressRange = targetReward.threshold - previousThreshold;
          const currentProgress = cartTotal - previousThreshold;
          progress = Math.min(100, Math.max(0, (currentProgress / progressRange) * 100));
        }
      }
    } else if (earnedRewards.length > 0 || earnedMilestones.length > 0 || activeRewards.some(r => isThresholdMet(r.threshold))) {
      // All rewards/milestones reached
      progress = 100;
    }

    return {
      currentTotal: cartTotal,
      nextThreshold: nextReward?.threshold || nextThresholdReward?.threshold || null,
      progress,
      earnedRewards,
      earnedMilestones,
      nextReward,
      hasActiveRewards: activeRewards.length > 0,
      nextMilestoneLabel: nextThresholdReward?.milestoneLabel || null,
    };
  } catch (error) {
    console.error('Error calculating reward progress:', error);
    return {
      currentTotal: cartTotal,
      nextThreshold: null,
      progress: 0,
      earnedRewards: [],
      earnedMilestones: [],
      nextReward: null,
      hasActiveRewards: false,
      nextMilestoneLabel: null,
    };
  }
};

